<script setup>
import {useBaseStore} from "~/stores/base";

/** props **/
const props = defineProps({
  info: {
    default: {},
    required: true
  }
})

// 图片的加载状态
const isLoaded = ref(false)

/** computed **/
const isMobile = computed(() => useBaseStore().getterIsMobile)
// 图片加载完成时的回调
const handleLoad = () => {
  isLoaded.value = true
}
// 图片加载失败时的回调
const handleError = () => {
  console.error("图片加载失败")
}

onMounted(() => {
  const img = document.querySelector('.cover-img');
  if (img?.complete) {
    handleLoad();
  }
});
</script>

<template>
  <div class="module-list-overview-mobile" v-if="isMobile">
    <div class="overview-top">
      <!-- 占位符 -->
      <div v-if="!isLoaded" class="cover-image-skeleton"/>
      <img
        class="cover-img"
        :class="{'cover-img-loaded': isLoaded}"
        :src="info.cover?.url"
        alt="cover"
        loading="lazy"
        @load="handleLoad"
        @error="handleError"
      >
    </div>
    <div class="overview-bottom">
      <div class="title">{{ info.name }}</div>
      <div class="desc">
        {{ info.brandBriefIntroductionAndStory }}
      </div>
      <div class="location" v-if="info.originName">
        <img src="@/assets/img/icon-location.svg" alt="location">
        <span>{{ info.originName }}</span>
      </div>
    </div>
  </div>
  <div class="module-list-overview-desktop" v-else>
    <div class="overview-left">
      <!-- 占位符 -->
      <div v-if="!isLoaded" class="cover-image-skeleton"/>
      <img
        class="cover-img"
        :class="{'cover-img-loaded': isLoaded}"
        :src="info.cover?.url"
        alt="cover"
        loading="lazy"
        @load="handleLoad"
        @error="handleError"
      >
    </div>
    <div class="overview-right">
      <div class="title">
        {{ info.name }}
      </div>
      <div class="desc">
        {{ info.brandBriefIntroductionAndStory }}
      </div>
      <div class="location" v-if="info.originName">
        <img src="@/assets/img/icon-location.svg" alt="location">
        <span>{{ info.originName }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "src/assets/config";

.module-list-overview-mobile {
  padding: 8px 16px;

  .overview-top {
    display: flex;
    position: relative;

    .cover-image-skeleton {
      flex-shrink: 0;

      position: absolute;
      left: 0;
      top: 0;

      width: 100%;
      aspect-ratio: 1 / 1.25;
      background: linear-gradient(90deg, var(--loader-background-color) 25%, var(--loader-highlight-color) 50%, var(--loader-background-color) 75%);
      background-size: 200% 100%;
      animation: loading 2s infinite ease-in-out;
    }

    @keyframes loading {
      0% {
        background-position: 200% 0;
      }
      100% {
        background-position: -200% 0;
      }
    }

    .cover-img {
      flex-shrink: 0;
      width: 100%;
      aspect-ratio: 1 / 1.25;
      object-fit: cover;
      display: block;
      transition: opacity 0.3s ease-in-out;
      opacity: 0; /* 初始隐藏 */
    }

    /* 图片加载完成后显示 */
    .cover-img-loaded {
      opacity: 1;
    }
  }

  .overview-bottom {
    display: flex;
    flex-direction: column;

    .title {
      /* Eyebrow/medium */
      font-family: "TWK Lausanne";
      font-weight: 650;
      font-size: 14px;
      font-style: normal;
      line-height: 18px; /* 128.571% */
      letter-spacing: -0.056px;
      text-transform: uppercase;

      margin-top: 8px;
    }

    .desc {
      font-family: "TWK Lausanne";
      font-size: 12px;
      font-style: normal;
      font-weight: 300;
      line-height: normal;
      color: $color-gray-600;

      margin-top: 5px;
    }

    .location {
      /* Text xxs/bold */
      font-family: "TWK Lausanne";
      font-weight: 650;
      font-size: 10px;
      font-style: normal;
      line-height: normal;

      display: flex;
      align-items: center;
      align-self: flex-end;

      margin-top: 30px;

      img {
        width: 20px;
        height: 20px;
        margin-right: 5px;
      }
    }
  }
}

.module-list-overview-desktop {
  width: $content-1280-width;
  margin-top: 16px;
  margin-left: auto;
  margin-right: auto;
  display: flex;

  .overview-left {
    display: flex;
    position: relative;

    .cover-image-skeleton {
      flex-shrink: 0;

      position: absolute;
      left: 0;
      top: 0;

      width: 206px;
      height: 257.5px;
      background: linear-gradient(90deg, var(--loader-background-color) 25%, var(--loader-highlight-color) 50%, var(--loader-background-color) 75%);
      background-size: 200% 100%;
      animation: loading 2s infinite ease-in-out;
    }

    @keyframes loading {
      0% {
        background-position: 200% 0;
      }
      100% {
        background-position: -200% 0;
      }
    }

    .cover-img {
      flex-shrink: 0;
      width: 206px;
      height: 257.5px;
      object-fit: cover;
      display: block;
      transition: opacity 0.3s ease-in-out;
      opacity: 0; /* 初始隐藏 */
    }

    /* 图片加载完成后显示 */
    .cover-img-loaded {
      opacity: 1;
    }
  }

  .overview-right {
    margin-left: 16px;

    .title {
      /* Eyebrow/medium */
      font-family: "TWK Lausanne";
      font-weight: 650;
      font-size: 14px;
      font-style: normal;
      line-height: 18px; /* 128.571% */
      letter-spacing: -0.056px;
      text-transform: uppercase;
    }

    .desc {
      max-width: 584px;

      font-family: "TWK Lausanne";
      font-size: 12px;
      font-style: normal;
      font-weight: 300;
      line-height: normal;
      color: $color-gray-600;

      margin-top: 5px;
    }

    .location {
      /* Text xxs/bold */
      font-family: "TWK Lausanne";
      font-weight: 650;
      font-size: 10px;
      font-style: normal;
      line-height: normal;

      display: flex;
      align-items: center;
      align-self: flex-end;

      margin-top: 30px;

      img {
        width: 20px;
        height: 20px;
        margin-right: 5px;
      }
    }
  }
}
</style>
